import { createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeckPlans, type OrderBy } from '../hooks/useDeckPlans.ts';
import { useDataViewSearch } from '../../../hooks/useDataViewSearch.ts';
import { useDataViewTableLogic } from '../../../hooks/useDataViewTableLogic.ts';
import DataPageContent from '../../../components/data/DataPageContent.tsx';
import NetexId from '../../../components/NetexId.tsx';
import type { ColumnDefinition } from '../../../components/data/dataTableTypes.ts';
import type { FilterDefinition } from '../../../components/search/searchTypes.ts';
import type { DeckPlan } from '../types/deckPlanTypes.ts';
import { getDeckPlanSortValue } from '../utils/deckPlanSortValue.ts';
import { useDeckPlanUrlSelection } from '../hooks/useDeckPlanUrlSelection.tsx';
import { deckPlanSelectedHref } from '../utils/deckPlanUrlParams.ts';

const deckPlanColumns: ColumnDefinition<DeckPlan, OrderBy>[] = [
  {
    id: 'name',
    headerLabel: 'deckPlans.field.name',
    isSortable: true,
    renderCell: item => item.name?.value,
    display: 'always',
  },
  {
    id: 'id',
    headerLabel: 'deckPlans.field.id',
    isSortable: true,
    renderCell: item =>
      createElement(NetexId, { id: item.id, version: item.version, size: 'small' }),
    display: 'always',
  },
];

const getDeckPlanFilterKey = (item: DeckPlan): string => item.id;

const DeckPlanFilters: FilterDefinition[] = [
  { id: 'parentDeckPlan', labelKey: 'types.parent', defaultLabel: 'Parent Vehicle Type' },
  { id: 'railVehicle', labelKey: 'types.train', defaultLabel: 'Train' },
  { id: 'metroVehicle', labelKey: 'types.metro', defaultLabel: 'Metro' },
  { id: 'onstreetBus', labelKey: 'types.bus', defaultLabel: 'Bus' },
  { id: 'onstreetTram', labelKey: 'types.tram', defaultLabel: 'Tram' },
  { id: 'ferryStop', labelKey: 'types.ferry', defaultLabel: 'Ferry' },
  { id: 'harbourPort', labelKey: 'types.harbour', defaultLabel: 'Harbour' },
  { id: 'liftStation', labelKey: 'types.lift', defaultLabel: 'Lift' },
];

/** Whole-row click opens the read-only sidebar via `/deck-plans?selected=<id>`. */
const useDeckPlanRowClick = () => {
  const navigate = useNavigate();
  return (item: DeckPlan) => navigate(deckPlanSelectedHref(item.id));
};

export const deckPlanViewConfig = {
  useData: useDeckPlans,
  useSearchRegistration: useDataViewSearch<DeckPlan>,
  useTableLogic: useDataViewTableLogic,
  PageContentComponent: DataPageContent,
  columns: deckPlanColumns,
  getFilterKey: getDeckPlanFilterKey,
  getSortValue: getDeckPlanSortValue,
  filters: () => DeckPlanFilters,
  titleKey: 'deckPlans.title',
  useUrlEffect: useDeckPlanUrlSelection,
  useRowClick: useDeckPlanRowClick,
};
